import React from "react";
import { fetchPullPlanScopeMembers } from "../../../../api/clientV2";
import type {
  DraftPayload,
  PendingTaktification,
  PullPlanSectionContext,
  PullTargetChoice,
  ScopeMember,
} from "../../../../models";
import { stageRezone } from "../../draft/draftOps";
import type { ActivityInstance } from "../../draft/types";
import { deriveMemberStates, type MemberState } from "../../ScopeLedger/laneState";
import { deriveDefaultLevel, leafMiscode, leafModalTakt } from "../../WbsBoardView/leafLevel";
import type { LeafTakt } from "../../WbsBoardView/leafLevel";
import type { ProtoNewAct } from "../PullPlanGrid";

interface ScopeLeaf {
  key: string;
  label: string;
  path: string[];
}

interface Args {
  scheduleId: number;
  context: PullPlanSectionContext;
  depEditorSection?: string;
  scopeMembers: ScopeMember[] | null;
  setScopeMembers: React.Dispatch<React.SetStateAction<ScopeMember[] | null>>;
  /** Comma-joined scope WBS ids; the membership fetch key. Empty ⇒ no scope. */
  scopeIdsKey: string;
  pendingOps: DraftPayload;
  setPendingOps: React.Dispatch<React.SetStateAction<DraftPayload>>;
  instances: ActivityInstance[];
  protoNewActs: ProtoNewAct[];
  protoCalendar: string[];
  protoRowOverrides: Map<number, string>;
  protoExtraRows: string[];
  leafLevelOverrides: Map<string, string>;
  setLeafLevelOverrides: React.Dispatch<React.SetStateAction<Map<string, string>>>;
  notaktLeaves: Set<string>;
  setNotaktLeaves: React.Dispatch<React.SetStateAction<Set<string>>>;
  levelUiActive: boolean;
  pullTarget: PullTargetChoice | null;
  pushUndo: (label?: string) => void;
}

export interface ScopeMembersState {
  memberStateById: Map<number, MemberState>;
  protoMembers: ScopeMember[];
  allScopeLeaves: ScopeLeaf[];
  membersByLeaf: Map<string, ScopeMember[]>;
  leafTaktByLeaf: Map<string, LeafTakt>;
  effectiveLevelByLeaf: Map<string, string | null>;
  miscodeCountByLeaf: Map<string, number>;
  targetByLeaf: Map<string, { code: string; date: string | null }>;
  handleSetLeafLevel: (leafKey: string, level: string) => void;
  handleToggleNotakt: (leafKey: string) => void;
}

export function useScopeMembers({
  scheduleId,
  context,
  depEditorSection,
  scopeMembers,
  setScopeMembers,
  scopeIdsKey,
  pendingOps,
  setPendingOps,
  instances,
  protoNewActs,
  protoCalendar,
  protoRowOverrides,
  protoExtraRows,
  leafLevelOverrides,
  setLeafLevelOverrides,
  notaktLeaves,
  setNotaktLeaves,
  levelUiActive,
  pullTarget,
  pushUndo,
}: Args): ScopeMembersState {
  // Membership truth for those leaves. Re-fetched when the committed context
  // does (post-publish reload), so placed members leave the lane for good.
  React.useEffect(() => {
    if (!scopeIdsKey) {
      setScopeMembers(null);
      return;
    }
    let cancelled = false;
    fetchPullPlanScopeMembers(scheduleId, scopeIdsKey.split(",").map(Number))
      .then((members) => {
        if (!cancelled) setScopeMembers(members);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // setScopeMembers is a stable setState setter
  }, [scheduleId, scopeIdsKey, context]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every scope member's side of the plan: planned members live on the board;
  // the rest are the lane's unresolved / kept / foreign / completed ghosts.
  const memberStateById = React.useMemo(() => {
    if (!scopeMembers) return new Map<number, MemberState>();
    const placedBarIds = new Set<number>();
    for (const i of instances) {
      if (i.source_activity_id != null) placedBarIds.add(i.source_activity_id);
    }
    return deriveMemberStates(
      scopeMembers,
      depEditorSection ?? "",
      placedBarIds,
      pendingOps,
    );
  }, [scopeMembers, depEditorSection, instances, pendingOps]);

  // Scope members + the staged drops as synthetic members (negative ids), so
  // the board, the lane list, and the counts all show the new work.
  const protoMembers = React.useMemo<ScopeMember[]>(() => {
    const staged = new Set(
      pendingOps.pending_activity_deletes.map((d) => d.activity_id),
    );
    const renames = new Map(
      pendingOps.pending_rename_edits.map((r) => [r.activity_id, r.name]),
    );
    const durations = new Map(
      pendingOps.pending_duration_edits.map((d) => [d.activity_id, d.duration_days]),
    );
    const takts = new Map(
      pendingOps.pending_taktifications.map((t) => [t.activity_id, t]),
    );
    const leafPathByKey = new Map<string, { path: string[]; nodeId: number }>();
    for (const m of scopeMembers ?? []) {
      const path = m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"];
      leafPathByKey.set(path.join(" "), { path, nodeId: m.wbs_node_id });
    }
    for (const label of protoExtraRows) {
      leafPathByKey.set(label, { path: [label], nodeId: 0 });
    }
    const out = (scopeMembers ?? [])
      .filter((m) => !staged.has(m.activity_id))
      .map((m) => {
        const name = renames.get(m.activity_id);
        const dur = durations.get(m.activity_id);
        const rowKey = protoRowOverrides.get(m.activity_id);
        const row = rowKey != null ? leafPathByKey.get(rowKey) : undefined;
        const takt = takts.get(m.activity_id);
        if (name == null && dur == null && row == null && takt == null) return m;
        return {
          ...m,
          task_name: name ?? m.task_name,
          original_duration: dur ?? m.original_duration,
          takt_work: takt ? takt.takt_work : m.takt_work,
          // Fold the STAGED Takt level so the mis-code chip/count clears after a
          // Set-level / Reconcile (the original member's takt_c never updates).
          takt_a: takt && takt.takt_a != null ? takt.takt_a : m.takt_a,
          takt_b: takt && takt.takt_b != null ? takt.takt_b : m.takt_b,
          takt_c: takt && takt.takt_c != null ? takt.takt_c : m.takt_c,
          wbs_path: row ? row.path : m.wbs_path,
          wbs_node_id: row ? row.nodeId : m.wbs_node_id,
        };
      });
    protoNewActs.forEach((n, i) => {
      if (n.deleted) return;
      const endIdx = Math.min(n.startIndex + n.durationDays - 1, protoCalendar.length - 1);
      out.push({
        activity_id: -(i + 1),
        task_code: "NEW",
        task_name: n.name,
        status: "TK_NotStart",
        restart_date: protoCalendar[n.startIndex] ?? null,
        reend_date: protoCalendar[endIdx] ?? null,
        actual_start: null,
        actual_finish: null,
        original_duration: n.durationDays,
        takt_section_name: depEditorSection ?? "",
        takt_a: "",
        takt_b: "",
        takt_c: "",
        takt_work: n.code,
        wbs_node_id: n.wbsNodeId,
        wbs_path: n.wbsPath,
      });
    });
    return out;
  }, [scopeMembers, protoNewActs, protoCalendar, depEditorSection, pendingOps.pending_activity_deletes, pendingOps.pending_rename_edits, pendingOps.pending_duration_edits, pendingOps.pending_taktifications, protoRowOverrides, protoExtraRows]);

  // Every scope leaf, derived from the FULL member list (deletes included) —
  // the board renders a row per leaf even when everything on it was wiped.
  const allScopeLeaves = React.useMemo<ScopeLeaf[]>(() => {
    const seen = new Map<string, ScopeLeaf>();
    for (const m of scopeMembers ?? []) {
      const path = m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"];
      const key = path.join(" ");
      if (!seen.has(key)) {
        seen.set(key, { key, label: path[path.length - 1], path });
      }
    }
    return Array.from(seen.values());
  }, [scopeMembers]);

  const membersByLeaf = React.useMemo(() => {
    const m = new Map<string, ScopeMember[]>();
    for (const mem of scopeMembers ?? []) {
      const path = mem.wbs_path.length > 0 ? mem.wbs_path : ["(no WBS node)"];
      const key = path.join(" ");
      const list = m.get(key);
      if (list) list.push(mem);
      else m.set(key, [mem]);
    }
    return m;
  }, [scopeMembers]);
  // Each leaf's real Takt identity, read from its members (modal). Drives the
  // section/zone context chip AND the default level — so an already-coded leaf
  // shows "UNIT · BLKR · LVL3" with zero clicks. Always computed (display is
  // never gated); the EDIT affordance stays scoped via levelUiActive below.
  const leafTaktByLeaf = React.useMemo(() => {
    const m = new Map<string, LeafTakt>();
    for (const leaf of allScopeLeaves) {
      m.set(leaf.key, leafModalTakt(membersByLeaf.get(leaf.key) ?? []));
    }
    return m;
  }, [allScopeLeaves, membersByLeaf]);
  // The WBS leaf is the source of truth for its level: prefer the user's
  // override, then the leaf's OWN name (a "Bottom Floor" leaf is LVL1 even if its
  // activities are mis-coded LVL3), and only fall back to the members' modal
  // Takt_C when the name carries no floor signal (e.g. "BlkR-3"). Disagreements
  // surface as the ⚠ miscode count below — never silently believed.
  const effectiveLevelByLeaf = React.useMemo(() => {
    const m = new Map<string, string | null>();
    for (const leaf of allScopeLeaves) {
      const override = leafLevelOverrides.get(leaf.key);
      m.set(
        leaf.key,
        override ??
          deriveDefaultLevel(leaf.label) ??
          leafTaktByLeaf.get(leaf.key)?.takt_c ??
          null,
      );
    }
    return m;
  }, [allScopeLeaves, leafLevelOverrides, leafTaktByLeaf]);
  const miscodeCountByLeaf = React.useMemo(() => {
    const m = new Map<string, number>();
    // The original member's takt_c never changes; a Set-level / Reconcile stages
    // the corrected level. Reflect that staged value so the ⚠ count drops to
    // zero as the affordance promises.
    const stagedTaktC = new Map<number, string | null>();
    for (const t of pendingOps.pending_taktifications) {
      if (t.takt_c != null) stagedTaktC.set(t.activity_id, t.takt_c);
    }
    for (const leaf of allScopeLeaves) {
      if (notaktLeaves.has(leaf.key)) continue;
      const eff = effectiveLevelByLeaf.get(leaf.key) ?? null;
      const members = (membersByLeaf.get(leaf.key) ?? []).map((mem) =>
        stagedTaktC.has(mem.activity_id)
          ? { ...mem, takt_c: stagedTaktC.get(mem.activity_id) ?? mem.takt_c }
          : mem,
      );
      const ids = leafMiscode(members, eff);
      if (ids.length > 0) m.set(leaf.key, ids.length);
    }
    return m;
  }, [
    allScopeLeaves,
    effectiveLevelByLeaf,
    membersByLeaf,
    notaktLeaves,
    pendingOps.pending_taktifications,
  ]);

  // Set a leaf's level: stage takt_c on every non-completed member (keep
  // takt_a/b/work) so publish stamps the level. One undo per click.
  const handleSetLeafLevel = React.useCallback(
    (leafKey: string, level: string) => {
      const value = level.trim();
      if (!value) return;
      pushUndo("Set level");
      setLeafLevelOverrides((prev) => {
        const next = new Map(prev);
        next.set(leafKey, value);
        return next;
      });
      const members = membersByLeaf.get(leafKey) ?? [];
      const takts: PendingTaktification[] = members
        .filter((mem) => mem.status !== "TK_Complete" && mem.takt_work)
        .map((mem) => ({
          activity_id: mem.activity_id,
          takt_a: mem.takt_a || null,
          takt_b: mem.takt_b || null,
          takt_c: value,
          takt_work: mem.takt_work,
        }));
      if (takts.length > 0) setPendingOps((prev) => stageRezone(prev, takts));
    },
    // setPendingOps / setLeafLevelOverrides are stable setState setters
    [membersByLeaf, pushUndo], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Reconcile: set the mis-coded members ONLY to the leaf's effective level.
  // Toggle "not Takt work": flip membership and clear any staged level
  // taktifications for the leaf's members (so it drops off the level staging).
  const handleToggleNotakt = React.useCallback(
    (leafKey: string) => {
      pushUndo("Not-Takt toggle");
      const memberIds = new Set(
        (membersByLeaf.get(leafKey) ?? []).map((mem) => mem.activity_id),
      );
      setNotaktLeaves((prev) => {
        const next = new Set(prev);
        const turningOn = !next.has(leafKey);
        if (turningOn) {
          next.add(leafKey);
          // Drop the leaf's level override and any staged level taktifications.
          setLeafLevelOverrides((lv) => {
            if (!lv.has(leafKey)) return lv;
            const m = new Map(lv);
            m.delete(leafKey);
            return m;
          });
          setPendingOps((prev) => ({
            ...prev,
            pending_taktifications: prev.pending_taktifications.filter(
              (t) => !memberIds.has(t.activity_id),
            ),
          }));
        } else {
          next.delete(leafKey);
        }
        return next;
      });
    },
    // setNotaktLeaves / setLeafLevelOverrides / setPendingOps are stable setState setters
    [membersByLeaf, pushUndo], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Per-leaf pull-target chips ("«27» 6/8") on the zone headers — resolved
  // from the entry flow's pull_target via each leaf's wbs id.
  const targetByLeaf = React.useMemo(() => {
    const out = new Map<string, { code: string; date: string | null }>();
    if (!pullTarget || pullTarget.mode === "skip" || !scopeMembers) return out;
    const keyByWbsId = new Map<number, string>();
    for (const m of scopeMembers) {
      if (!keyByWbsId.has(m.wbs_node_id)) {
        const path = m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"];
        keyByWbsId.set(m.wbs_node_id, path.join(" "));
      }
    }
    for (const pl of pullTarget.per_leaf) {
      const key = pl.wbs_id != null ? keyByWbsId.get(pl.wbs_id) : undefined;
      if (key) {
        out.set(key, {
          code: pullTarget.mode === "milestone" ? pullTarget.work_code : "",
          date: pl.date ?? null,
        });
      }
    }
    return out;
  }, [pullTarget, scopeMembers]);

  return {
    memberStateById,
    protoMembers,
    allScopeLeaves,
    membersByLeaf,
    leafTaktByLeaf,
    effectiveLevelByLeaf,
    miscodeCountByLeaf,
    targetByLeaf,
    handleSetLeafLevel,
    handleToggleNotakt,
  };
}
